import { useCallback, useState } from 'react';

import { Dialog, SwipeableViews } from '@cast/design-system';
import { Cluster } from '@cast/types';

import { isE2E } from '@cast/utils';

import { EnablingSlide } from './_components/EnablingSlide';
import { ErrorSlide } from './_components/ErrorSlide';
import { RunTheScriptSlide } from './_components/RunTheScriptSlide';
import { SuccessSlide } from './_components/SuccessSlide';
import { useWasSecurityEnabled } from '../../_hooks/useWasSecurityEnabled';

import { ClusterProvider } from '@cast/shared-cluster-context-data-access';

type Props = {
  cluster: Cluster;
  destroy: () => void;
  onEnabled?: () => void;
};

export const AssignSecurityCredentialsDialog = ({
  destroy,
  cluster,
  onEnabled: _onEnabled,
}: Props) => {
  const [activeSlide, setActiveSlide] = useState<
    'runTheScript' | 'enabling' | 'success' | 'error'
  >('runTheScript');

  const onEnabled = useCallback(() => {
    setActiveSlide('success');
    _onEnabled?.();
  }, [_onEnabled]);

  const onError = useCallback(() => {
    setActiveSlide('error');
  }, []);

  useWasSecurityEnabled({
    clusterId: cluster.id,
    onSuccess: onEnabled,
    onError,
    enabled: activeSlide === 'enabling',
  });

  return (
    <Dialog
      open
      onClose={destroy}
      disableCloseOnBackdropClick
      testId="assign-security-credentials-dialog"
    >
      <ClusterProvider cluster={cluster}>
        <SwipeableViews
          slides={{
            runTheScript: (
              <RunTheScriptSlide onClose={destroy} clusterId={cluster.id} />
            ),
            enabling: <EnablingSlide onClose={destroy} />,
            error: (
              <ErrorSlide
                title="Failed to enable Kubernetes & cloud security"
                onRetry={() => setActiveSlide('runTheScript')}
                onClose={destroy}
              />
            ),
            success: <SuccessSlide onClose={destroy} />,
          }}
          disableAnimations={isE2E()}
          activeSlide={activeSlide}
          onSlideChange={setActiveSlide}
        />
      </ClusterProvider>
    </Dialog>
  );
};
